import logger, { getByodSessionId } from "./telemetry";
import { telemetryManager } from "./telemetryManager";
import { PolicyEvaluationStep } from '@/models/flw-orchestrator.types';
import { logDev as debugLog, warnDev } from '@/logging/devConsole';

type ErrorReason =
  | "kill-switch-ecs"
  | "api-failure"
  | "component-error"
  | "ecs client failure"
  | "auth redirect failure"
  | "mfa api failure"
  | "mam api failure";
type PolicyResultPayload = {
  policyType?: string | null;
  isRequired?: boolean;
  reason?: string | null;
  details?: string | null;
};

// Main scenario name for the complete BYOD wizard flow (including feedback)
const MAIN_SCENARIO = "ByodWizardComplete";

// Maps screen names to scenario step names
// Note: Feedback screens (FeedbackThanksScreen, FeedbackSubmittedScreen) are NOT in scenario
// They are tracked in UserBI only, since wizard completes at AppDownloadScreen
const SCREEN_TO_STEP_MAP: Record<string, string> = {
  DeviceSelection: "device-selection",
  AuthRedirectHandler: "auth-redirect-complete",
  AuthenticatorSetupScreen: "auth-setup",
  CompanyPortalInstall: "company-portal",
  AppDownloadScreen: "app-download",
};

// Feedback source types
export type FeedbackSource = "thanks-screen" | "footer" | "device-selection";

// Wizard-related screens that should lazily start the scenario if accessed directly (e.g., deep link, redirect)
const WIZARD_SCREENS = new Set([
  "DeviceSelection",
  "AuthenticatorSetupScreen",
  "CompanyPortalInstall",
  "AppDownloadScreen"
]);

function ensureWizardScenario() {
  if (!telemetryManager.hasActiveScenario()) {
    debugLog('[TelemetryEvents] ensureWizardScenario starting scenario:', MAIN_SCENARIO);
    telemetryManager.startScenario(MAIN_SCENARIO);
  }
}

function getCommonDatabag(): Record<string, unknown> {
  const byodSessionId = getByodSessionId();
  const lastCompletedStep = telemetryManager.getLastCompletedStep?.();
  return {
    ...(byodSessionId && { byodSessionId }),
    ...(lastCompletedStep && { lastCompletedStep }),
  };
}

export function logScreenRender(screenName: string, additionalUserbiData?: Record<string, any>) {
  debugLog('[TelemetryEvents] logScreenRender called for screen:', screenName);

  // Always log UserBI screen view with device type if available
  const deviceType = telemetryManager.getDeviceType?.();
  const userbiData = {
    ...getCommonDatabag(),
    ...(deviceType && { deviceType }),
    ...additionalUserbiData
  };

  logger.userbi.recordView(
    {
      panelType: screenName,
      panelRegion: "Main"
    },
    Object.keys(userbiData).length > 0 ? userbiData : undefined
  );

  // Check if this is the start of the main wizard flow
  if (screenName === "WelcomeScreen") {
    // Start the main scenario if not already started
    if (!telemetryManager.hasActiveScenario()) {
      debugLog('[TelemetryEvents] Starting main scenario for welcome screen');
      telemetryManager.startScenario(MAIN_SCENARIO);
    }
    telemetryManager.markStep("welcome");
    return;
  }

  // For FeedbackThanksScreen and FeedbackSubmittedScreen, only log UserBI (not scenario)
  // Wizard scenario completes at AppDownloadScreen
  if (screenName === "FeedbackThanksScreen" || screenName === "FeedbackSubmittedScreen") {
    debugLog('[TelemetryEvents] Feedback screen - UserBI only, scenario already completed at AppDownloadScreen');
    return;
  }

  // For other wizard screens, ensure scenario is active (handles deep links or mid-flow entry)
  if (!telemetryManager.hasActiveScenario() && WIZARD_SCREENS.has(screenName)) {
    // Lazy start scenario for mid-flow entry (e.g., user lands on DeviceSelection via deep link)
    ensureWizardScenario();
  }

  if (telemetryManager.hasActiveScenario()) {
    const stepName = SCREEN_TO_STEP_MAP[screenName] || screenName.toLowerCase();
    debugLog('[TelemetryEvents] Marking step:', stepName);
    telemetryManager.markStep(stepName);
  }
}

export function logButtonClick(screenName: string, buttonName: string) {
  // Always log UserBI action with device type if available
  const deviceType = telemetryManager.getDeviceType?.();
  const userbiData = {
    ...getCommonDatabag(),
    ...(deviceType && { deviceType }),
  };
  
  logger.userbi.recordAction(
    {
      panelType: screenName,
      panelRegion: "Main",
      actionGesture: "click", 
      actionOutcome: "success",
      actionScenario: `${buttonName}_clicked`,
      actionScenarioType: "user-action",
      actionWorkLoad: screenName,
      actionSubWorkLoad: buttonName
    },
    userbiData
  );
}

export function logLinkClick(screenName: string, linkName: string, extraData?: Record<string, unknown>) {
  const deviceType = telemetryManager.getDeviceType?.();
  const userbiData = {
    ...getCommonDatabag(),
    ...(deviceType && { deviceType }),
    ...extraData,
  };

  logger.userbi.recordAction(
    {
      panelType: screenName,
      panelRegion: "Main",
      actionGesture: "click",
      actionOutcome: "success",
      actionScenario: `${linkName}_link_clicked`,
      actionScenarioType: "user-action",
      actionWorkLoad: screenName,
      actionSubWorkLoad: linkName
    },
    Object.keys(userbiData).length > 0 ? userbiData : undefined
  );
}

export function logBackNavigation(screenName: string) {
  // Store last back-navigate screen so abandonment events know where user reversed from
  sessionStorage.setItem('lastBackScreen', screenName);
  logButtonClick(screenName, 'back');
}

export function logBrowserBackButton(screenName: string) {
  const deviceType = telemetryManager.getDeviceType?.();
  const userbiData = {
    ...getCommonDatabag(),
    ...(deviceType && { deviceType }),
  };

  logger.userbi.recordAction(
    {
      panelType: screenName,
      panelRegion: "Main",
      actionGesture: "click",
      actionOutcome: "success",
      actionScenario: "browser_back_clicked",
      actionScenarioType: "user-action",
      actionWorkLoad: screenName,
      actionSubWorkLoad: "browser-back"
    },
    userbiData
  );
}

export function completeScenario(status: "success" | "failure" | "abandoned" = "success", reason?: string) {
  debugLog('[TelemetryEvents] completeScenario called with status:', status, 'reason:', reason);
  // Complete the main scenario with all collected DataBagProperties
  if (telemetryManager.hasActiveScenario()) {
    telemetryManager.completeScenario(status, reason);
  } else {
    warnDev('[TelemetryEvents] Cannot complete scenario - no active scenario');
  }
}

// Entry source tracking
export function collectEntrySource(source: string) {
  debugLog('[TelemetryEvents] collectEntrySource called with source:', source);
  ensureWizardScenario();
  telemetryManager.collectEntrySource(source);
}

// New helper functions for data collection
export function collectDeviceSelection(deviceType: "android" | "ios") {
  debugLog('[TelemetryEvents] collectDeviceSelection called with deviceType:', deviceType);
  ensureWizardScenario();
  telemetryManager.collectDeviceSelection(deviceType);
}

export function collectAuthStatus(status: "set" | "not-set" | "unknown", mfaRequired?: boolean) {
  debugLog('[TelemetryEvents] collectAuthStatus called with status:', status, 'mfaRequired:', mfaRequired);
  ensureWizardScenario();
  telemetryManager.collectAuthStatus(status, mfaRequired);
}

export function collectCompanyPortalStatus(mamEnabled: boolean) {
  debugLog('[TelemetryEvents] collectCompanyPortalStatus called with mamEnabled:', mamEnabled);
  ensureWizardScenario();
  telemetryManager.collectCompanyPortalStatus(mamEnabled);
}

export function collectMfaPolicyResult(result: PolicyResultPayload) {
  debugLog('[TelemetryEvents] collectMfaPolicyResult called with result:', result);
  ensureWizardScenario();
  telemetryManager.collectMfaPolicyResult(result);
}

export function collectMamPolicyResult(result: PolicyResultPayload) {
  debugLog('[TelemetryEvents] collectMamPolicyResult called with result:', result);
  ensureWizardScenario();
  telemetryManager.collectMamPolicyResult(result);
}

export function collectTenantRegion(region: {
  region?: string | null;
  environment?: string | null;
}) {
  debugLog('[TelemetryEvents] collectTenantRegion called with region:', region);
  ensureWizardScenario();
  telemetryManager.collectTenantRegion(region);
}

export function collectAppDownloadData(data: { platform?: string | null }) {
  debugLog('[TelemetryEvents] collectAppDownloadData called with data:', data);
  ensureWizardScenario();
  telemetryManager.collectAppDownloadData(data);
}

export function trackAuthRedirect(started: boolean) {
  debugLog('[TelemetryEvents] trackAuthRedirect called with started:', started);
  if (started) {
    // Starting redirect mid-flow should still ensure scenario exists for timing
    ensureWizardScenario();
  }
  telemetryManager.trackAuthRedirect(started);
}

// Track user abandonment
export function trackAbandonment(reason: "browser-close" | "navigate-away" | "user-dropout") {
  debugLog('[TelemetryEvents] trackAbandonment called with reason:', reason);

  // Only track abandonment if scenario is still active (prevents duplicate abandonments after completion)
  if (telemetryManager.hasActiveScenario()) {
    // Append last back-navigation screen if user had clicked back before abandoning
    // This lets us distinguish e.g. "browser-close after backing out of MFA" vs normal drop
    const lastBackScreen = sessionStorage.getItem('lastBackScreen');
    const fullReason = lastBackScreen ? `${reason}:back-from-${lastBackScreen}` : reason;
    completeScenario("abandoned", fullReason);
  } else {
    debugLog('[TelemetryEvents] Ignoring abandonment - scenario already completed');
  }
}

// Track CPS policy check for feedback
export function logFeedbackPolicyCheck(
  location: 'footer' | 'feedback-form',
  result: 'allowed' | 'blocked' | 'error',
  details?: {
    cloudType?: string;
    errorMessage?: string;
    durationMs?: number;
    tenantId?: string;
  }
) {
  debugLog('[TelemetryEvents] logFeedbackPolicyCheck called with location:', location, 'result:', result, 'details:', details);

  const deviceType = telemetryManager.getDeviceType?.();
  const userbiData = {
    ...(deviceType && { deviceType }),
    feedbackPolicyLocation: location,
    feedbackPolicyResult: result,
    ...(details?.cloudType && { cloudType: details.cloudType }),
    ...(details?.errorMessage && { errorMessage: details.errorMessage }),
    ...(details?.durationMs && { policyCheckDurationMs: details.durationMs }),
    ...(details?.tenantId && { tenantId: details.tenantId }),
  };

  const outcome = result === 'error' ? 'failure' : 'success';
  const actionScenario = `feedback_policy_${result}`;

  logger.userbi.recordAction(
    {
      panelType: location === 'footer' ? 'Footer' : 'FeedbackForm',
      panelRegion: "Main",
      actionGesture: "evaluate",
      actionOutcome: outcome,
      actionScenario,
      actionScenarioType: "system-action",
      actionWorkLoad: "FeedbackCPS",
      actionSubWorkLoad: location
    },
    userbiData
  );
}

// Track when feedback link visibility changes
export function logFeedbackLinkVisibility(
  visible: boolean,
  reason: 'cps-allowed' | 'cps-blocked' | 'inline-active' | 'anonymous-user' | 'error',
  currentScreen?: string
) {
  debugLog('[TelemetryEvents] logFeedbackLinkVisibility called with visible:', visible, 'reason:', reason, 'screen:', currentScreen);

  const deviceType = telemetryManager.getDeviceType?.();
  const userbiData = {
    ...(deviceType && { deviceType }),
    feedbackLinkVisible: visible,
    visibilityReason: reason,
    ...(currentScreen && { currentScreen }),
  };

  logger.userbi.recordAction(
    {
      panelType: 'Footer',
      panelRegion: "Main",
      actionGesture: "display",
      actionOutcome: visible ? 'success' : 'suppressed',
      actionScenario: visible ? 'feedback_link_shown' : 'feedback_link_hidden',
      actionScenarioType: "system-action",
      actionWorkLoad: "FeedbackVisibility",
      actionSubWorkLoad: reason
    },
    userbiData
  );
}

// Track when user clicks feedback link in footer
export function logFeedbackLinkClicked(currentScreen?: string) {
  debugLog('[TelemetryEvents] logFeedbackLinkClicked called from screen:', currentScreen);

  const deviceType = telemetryManager.getDeviceType?.();
  const userbiData = {
    ...(deviceType && { deviceType }),
    feedbackSource: 'footer',
    ...(currentScreen && { currentScreen }),
  };

  logger.userbi.recordAction(
    {
      panelType: 'Footer',
      panelRegion: "Main",
      actionGesture: "click",
      actionOutcome: "success",
      actionScenario: "feedback_link_clicked",
      actionScenarioType: "user-action",
      actionWorkLoad: "Feedback",
      actionSubWorkLoad: currentScreen || "footer-link"
    },
    userbiData
  );
}

// Track when feedback modal/form is opened
export function logFeedbackModalOpened(source: FeedbackSource, authenticationType: 'aad' | 'anonymous', currentScreen?: string) {
  debugLog('[TelemetryEvents] logFeedbackModalOpened called with source:', source, 'auth:', authenticationType, 'screen:', currentScreen);

  const deviceType = telemetryManager.getDeviceType?.();
  const userbiData = {
    ...(deviceType && { deviceType }),
    feedbackSource: source,
    ...(currentScreen && { currentScreen }),
    authenticationType,
  };

  logger.userbi.recordAction(
    {
      panelType: source === 'thanks-screen' ? 'FeedbackThanksScreen' : 'Footer',
      panelRegion: "Main",
      actionGesture: "open",
      actionOutcome: "success",
      actionScenario: "feedback_modal_opened",
      actionScenarioType: "user-action",
      actionWorkLoad: "Feedback",
      actionSubWorkLoad: currentScreen || source
    },
    userbiData
  );
}

// Track feedback rating selected (1-5 star rating)
// NOTE: The Microsoft InApp Feedback SDK does not expose rating values through callbacks.
// This function is reserved for future use if rating tracking becomes available.
// Currently, we can only track submission success (with feedbackId), not individual ratings.
export function logFeedbackRatingSelected(source: FeedbackSource, rating: number) {
  debugLog('[TelemetryEvents] logFeedbackRatingSelected called with source:', source, 'rating:', rating);

  const deviceType = telemetryManager.getDeviceType?.();
  const userbiData = {
    ...(deviceType && { deviceType }),
    feedbackSource: source,
    feedbackRating: rating,
  };

  logger.userbi.recordAction(
    {
      panelType: source === 'thanks-screen' ? 'FeedbackThanksScreen' : 'Footer',
      panelRegion: "Main",
      actionGesture: "select",
      actionOutcome: "success",
      actionScenario: "feedback_rating_selected",
      actionScenarioType: "user-action",
      actionWorkLoad: "Feedback",
      actionSubWorkLoad: `rating-${rating}`
    },
    userbiData
  );
}

// Track feedback dismissal (cancel/close without submitting)
export function logFeedbackDismissed(source: FeedbackSource, wasSubmitted: boolean, currentScreen?: string) {
  debugLog('[TelemetryEvents] logFeedbackDismissed called with source:', source, 'wasSubmitted:', wasSubmitted, 'screen:', currentScreen);

  const deviceType = telemetryManager.getDeviceType?.();
  const userbiData = {
    ...(deviceType && { deviceType }),
    feedbackSource: source,
    ...(currentScreen && { currentScreen }),
    wasSubmitted,
  };

  logger.userbi.recordAction(
    {
      panelType: source === 'thanks-screen' ? 'FeedbackThanksScreen' : 'Footer',
      panelRegion: "Main",
      actionGesture: "dismiss",
      actionOutcome: wasSubmitted ? "success" : "cancelled",
      actionScenario: wasSubmitted ? "feedback_dismissed_after_submit" : "feedback_cancelled",
      actionScenarioType: "user-action",
      actionWorkLoad: "Feedback",
      actionSubWorkLoad: currentScreen || source
    },
    userbiData
  );
}

// Track feedback submission with source
// NOTE: Rating and comment text are not available from the SDK callbacks
export function logFeedbackSubmission(source: FeedbackSource, feedbackId?: string, currentScreen?: string) {
  debugLog('[TelemetryEvents] logFeedbackSubmission called with source:', source, 'feedbackId:', feedbackId, 'screen:', currentScreen);

  const deviceType = telemetryManager.getDeviceType?.();
  const userbiData = {
    ...(deviceType && { deviceType }),
    feedbackSource: source,
    ...(currentScreen && { currentScreen }),
    ...(feedbackId && { feedbackId }),
  };

  logger.userbi.recordAction(
    {
      panelType: source === 'thanks-screen' ? 'FeedbackThanksScreen' : 'Footer',
      panelRegion: "Main",
      actionGesture: "click",
      actionOutcome: "success",
      actionScenario: "feedback_submitted",
      actionScenarioType: "user-action",
      actionWorkLoad: "Feedback",
      actionSubWorkLoad: currentScreen || source
    },
    userbiData
  );
}

// Track overall feedback completion (sent as a separate telemetry event)
// This is independent of the main wizard scenario and captures the full feedback journey
export function logFeedbackCompletion(
  feedbackProvided: boolean,
  source: FeedbackSource,
  details?: {
    wasSubmitted?: boolean;
    wasCancelled?: boolean;
    feedbackId?: string;
    timeOnScreenMs?: number;
    authenticationType?: 'aad' | 'anonymous';
  }
) {
  debugLog('[TelemetryEvents] logFeedbackCompletion called:', { feedbackProvided, source, details });

  const deviceType = telemetryManager.getDeviceType?.();
  const userbiData = {
    ...(deviceType && { deviceType }),
    feedbackProvided,
    feedbackSource: source,
    ...(details?.wasSubmitted !== undefined && { wasSubmitted: details.wasSubmitted }),
    ...(details?.wasCancelled !== undefined && { wasCancelled: details.wasCancelled }),
    ...(details?.feedbackId && { feedbackId: details.feedbackId }),
    ...(details?.timeOnScreenMs !== undefined && { timeOnScreenMs: details.timeOnScreenMs }),
    ...(details?.authenticationType && { authenticationType: details.authenticationType }),
  };

  logger.userbi.recordAction(
    {
      panelType: 'FeedbackCompletion',
      panelRegion: "Main",
      actionGesture: feedbackProvided ? "complete" : "skip",
      actionOutcome: feedbackProvided ? "success" : "skipped",
      actionScenario: "feedback_journey_complete",
      actionScenarioType: "user-action",
      actionWorkLoad: "Feedback",
      actionSubWorkLoad: source
    },
    userbiData
  );
}

// Collect footer feedback click for scenario tracking
// Adds to wizard scenario (ensures scenario is started if needed)
export function collectFooterFeedbackClick(screen: string, cpsPolicyAllowed?: boolean) {
  debugLog('[TelemetryEvents] collectFooterFeedbackClick called - Screen:', screen, 'CPS allowed:', cpsPolicyAllowed);

  // Ensure wizard scenario is active (handles post-wizard footer clicks)
  ensureWizardScenario();

  if (telemetryManager.hasActiveScenario()) {
    debugLog('[TelemetryEvents] Adding footer feedback to wizard scenario');
    telemetryManager.collectFooterFeedbackClick(screen, cpsPolicyAllowed);
  } else {
    debugLog('[TelemetryEvents] WARNING: Could not start/find scenario for footer feedback');
  }
}

// Collect feedback completion for scenario tracking
// All feedback (footer during wizard, thanks-screen after wizard) goes into the wizard scenario
export function collectFeedbackCompletion(data: {
  feedbackProvided: boolean;
  feedbackSubmitted?: boolean;
  feedbackCancelled?: boolean;
  feedbackSource?: 'thanks-screen' | 'footer' | 'device-selection';
  feedbackId?: string;
  timeOnScreenMs?: number;
  authenticationType?: 'aad' | 'anonymous';
  errorMessage?: string;
  currentScreen?: string; // which screen the feedback was shown from (for footer feedback)
  timeSpentMs?: number; // time spent on footer feedback modal before submit/cancel
}) {
  debugLog('[TelemetryEvents] collectFeedbackCompletion called:', data);

  // Add to wizard scenario if active (should be active for both footer feedback and thanks-screen)
  if (telemetryManager.hasActiveScenario()) {
    const scenarioName = (telemetryManager as any).currentScenario;
    debugLog('[TelemetryEvents] Adding feedback completion to active scenario:', scenarioName);
    telemetryManager.collectFeedbackCompletion(data);
  } else {
    // No scenario active - might happen if user comes back after browser close
    debugLog('[TelemetryEvents] WARNING: No active scenario for feedback completion');
  }
}

// Complete feedback scenario (no-op now since wizard scenario completes at FeedbackSubmittedScreen)
// This function is called from FeedbackForm components but we now handle completion in logScreenRender
export function completeFeedbackScenario(outcome: 'success' | 'failure' | 'abandoned', reason?: string) {
  debugLog('[TelemetryEvents] completeFeedbackScenario called with outcome:', outcome, 'reason:', reason);
  debugLog('[TelemetryEvents] Ignoring - wizard scenario completes automatically at FeedbackSubmittedScreen');
  // Do nothing - the scenario will complete when FeedbackSubmittedScreen renders
}

// Error tracking
export function trackError(reason: ErrorReason, error?: unknown) {
  debugLog('[TelemetryEvents] trackError called with reason:', reason, 'error:', error);
  // Only track error if scenario is still active
  if (telemetryManager.hasActiveScenario()) {
    telemetryManager.recordFailure(reason, error);
    telemetryManager.completeScenario("failure", reason);
  } else {
    debugLog('[TelemetryEvents] Ignoring error - scenario already completed');
  }
}

export function recordRecoverableError(reason: ErrorReason, step?: PolicyEvaluationStep, attempt?: number, error?: unknown, durationMs?: number) {
  debugLog('[TelemetryEvents] recordRecoverableError called with reason:', reason, 'step:', step, 'attempt:', attempt, 'error:', error, 'durationMs:', durationMs);
  ensureWizardScenario();
  if (telemetryManager.hasActiveScenario()) {
    // For attempt 0 (initial call), use 'mfa-call' or 'mam-call'
    // For attempts 1+ (actual retries), use 'retry-mfa' or 'retry-mam'
    let baseStep: 'retry-mfa' | 'retry-mam' | 'mfa-call' | 'mam-call' | undefined;
    if (step === PolicyEvaluationStep.MFA) {
      baseStep = attempt === 0 ? 'mfa-call' : 'retry-mfa';
    } else if (step === PolicyEvaluationStep.MAM) {
      baseStep = attempt === 0 ? 'mam-call' : 'retry-mam';
    }
    telemetryManager.recordRecoverableError(reason, baseStep, attempt, error, durationMs);
  } else {
    debugLog('[TelemetryEvents] Recoverable error ignored - no active scenario');
  }
}

/**
 * Log retry screen step with attempt metadata while keeping scenario active
 */
export function logRetryStep(step: PolicyEvaluationStep, attempt: number) {
  debugLog('[TelemetryEvents] logRetryStep called with step:', step, 'attempt:', attempt);

  ensureWizardScenario();
  if (!telemetryManager.hasActiveScenario()) {
    debugLog('[TelemetryEvents] Retry step ignored - no active scenario even after ensure');
    return;
  }

  // For attempt 0 (initial call), don't log as retry - it will be logged by the API call itself
  // For attempt 4+, don't log as retry - max retries is 3, attempt 4 means Continue flow
  // Only log for actual retries (attempts 1, 2, 3)
  if (attempt === 0 || attempt > 3) {
    debugLog('[TelemetryEvents] Skipping retry step logging for attempt:', attempt, '- initial call or Continue flow tracked separately');
    return;
  }

  const baseStep = step === PolicyEvaluationStep.MFA ? 'retry-mfa' : 'retry-mam';
  const stepName = `${baseStep}-${attempt}`;

  telemetryManager.markStep(stepName);
  telemetryManager.recordRetryAttempt(baseStep as 'retry-mfa' | 'retry-mam', attempt);
}

/**
 * Start tracking policy evaluation loading time
 * Call this when API evaluation starts (loading screen shown)
 */
export function startPolicyEvaluationTiming(step: 'mfa' | 'mam') {
  debugLog('[TelemetryEvents] startPolicyEvaluationTiming called for:', step);
  ensureWizardScenario();
  telemetryManager.startPolicyEvaluation(step);
}

/**
 * End tracking policy evaluation loading time
 * Call this when the next screen renders after evaluation
 */
export function endPolicyEvaluationTiming(step: 'mfa' | 'mam') {
  debugLog('[TelemetryEvents] endPolicyEvaluationTiming called for:', step);
  if (telemetryManager.hasActiveScenario()) {
    telemetryManager.endPolicyEvaluation(step);
  }
}

/**
 * Start tracking page render time
 * Call this at the very beginning of component (before useEffect)
 */
export function startPageRenderTiming(screen: 'welcome' | 'device-selection' | 'app-download') {
  debugLog('[TelemetryEvents] startPageRenderTiming called for:', screen);
  ensureWizardScenario();
  if (telemetryManager.hasActiveScenario()) {
    telemetryManager.startPageRender(screen);
  }
}

/**
 * End tracking page render time
 * Call this when page is fully rendered (in useEffect)
 */
export function endPageRenderTiming(screen: 'welcome' | 'device-selection' | 'app-download') {
  debugLog('[TelemetryEvents] endPageRenderTiming called for:', screen);
  if (telemetryManager.hasActiveScenario()) {
    telemetryManager.endPageRender(screen);
  }
}
